import type { Conversation } from '../types';
import {
  effectiveConversationStatus,
  normalizeConversationStatus,
} from '../../../utils/texting';

type ConversationStatus =
  | 'new-message'
  | 'unresolved'
  | 'resolved'
  | 'invalid-phone';
type MessageStatus =
  | 'delivered'
  | 'undelivered'
  | 'failed'
  | 'invalid-phone'
  | 'mass-communication'
  | 'automated'
  | 'processing';

type Message = any;
type ComponentConversation = any;

export function conversationStatusToStatus(
  conversationStatus: string,
): ConversationStatus {
  const status = normalizeConversationStatus(conversationStatus);
  if (status === 'NEW') return 'new-message';
  if (status === 'UNRESOLVED') return 'unresolved';
  return 'resolved';
}

export function messageStatusToStatus(messageStatus: string): MessageStatus {
  if (messageStatus === 'DELIVERED' || messageStatus === 'RECEIVED')
    return 'delivered';
  if (messageStatus === 'UNDELIVERED') return 'undelivered';
  if (messageStatus === 'FAILED') return 'failed';
  if (messageStatus === 'BLOCKED_OPT_OUT') return 'failed';
  if (messageStatus === 'INVALID_PHONE_NUMBER') return 'invalid-phone';
  if (messageStatus === 'MASS_COMMUNICATION') return 'mass-communication';
  if (messageStatus === 'AUTOMATED') return 'automated';
  return 'processing';
}

export function conversationToComponentConversation(
  conversation: Conversation,
): ComponentConversation {
  const participantName =
    conversation.recipient.customer?.fullName ||
    conversation.recipient.prospect?.fullName ||
    'Unknown';
  const phoneNumber = conversation.recipient.phoneNumber || '';

  return {
    id: conversation.conversationId,
    recipients: [
      {
        id: conversation.conversationId,
        name: participantName,
        phone: phoneNumber,
      },
    ],
    lastReceivedMessage: conversation.lastMessage
      ? {
          content: conversation.lastMessage.content,
          dateDelivered: conversation.lastMessage.createdAt,
        }
      : null,
    status: conversationStatusToStatus(
      effectiveConversationStatus(conversation),
    ),
  };
}

export function messageDtoToMessage({
  messageDto,
  conversation,
  user,
  includeUser,
  includeAttachments,
  leslieEnabled,
}: {
  messageDto: Object,
  conversation: Conversation,
  user?: Object,
  includeUser?: boolean,
  includeAttachments: boolean,
  leslieEnabled?: boolean,
}): Message {
  const senderType =
    messageDto.type === 'OUTBOUND' ? 'property' : 'household-member';
  const participantName =
    conversation.recipient.customer?.fullName ||
    conversation.recipient.prospect?.fullName ||
    'Unknown';
  const propertyName = conversation.recipient.property?.name || 'Fortress';

  return {
    id: messageDto['_id'],
    content: messageDto.content,
    sender: senderType === 'household-member' ? participantName : propertyName,
    senderType,
    // The centralized hub flags the same message the household Texting tab does, so staff see
    // the hand-off wherever they are reading the conversation from.
    needsHelp: Boolean(leslieEnabled) && messageDto.needsHelp === true,
    dateDelivered: messageDto.updatedAt,
    deliveryFailureReason:
      messageDto.messageStatus === 'INVALID_PHONE_NUMBER'
        ? 'invalid-phone'
        : undefined,
    status: messageStatusToStatus(messageDto.messageStatus),
    isPhoneNumberChangeMsg:
      messageDto.content.startsWith('Phone number has changed from ') &&
      messageDto.type === 'INTERNAL',
    user: includeUser && user ? `${user.firstName} ${user.lastName}` : null,
    attachments: includeAttachments
      ? messageDto.attachments?.map((a) => ({
          type: 'image',
          url: a.url,
        }))
      : undefined,
  };
}
